import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TablePagination from '@mui/material/TablePagination'
import TextField from '@mui/material/TextField'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { SensemakerPageTitle } from '@/shared/components/SensemakerPageTitle'
import { StandardPageTitleRow } from '@/shared/components/StandardPageTitleRow'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import { useCampaigns } from '../api/campaign-queries'
import { useCampaignListInstrumentMetrics } from '../api/useCampaignListInstrumentMetrics'
import { CampaignCard } from '../components/CampaignCard'
import { CampaignListEmptyState } from '../components/CampaignListEmptyState'
import { CampaignListPageSkeleton } from '../components/CampaignListPageSkeleton'
import { CampaignListToolbar } from '../components/CampaignListToolbar'
import { CampaignStatusFilter } from '../components/CampaignStatusFilter'
import { DirectInstrumentBuildingPanel } from '../components/DirectInstrumentBuildingPanel'
import {
  enrichCampaignsWithInstrumentMetrics,
} from '../lib/campaign-list-metrics'
import {
  matchesCampaignLandingSearch,
  matchesLandingFilter,
  sortCampaignsByUpdatedDesc,
  type CampaignLandingFilter,
} from '../lib/campaign-landing-display'
import { useCampaignLandingSearch } from '../lib/campaignLandingSearch'
import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'
import type { Campaign } from '../types'

const DEFAULT_ROWS_PER_PAGE = 6
const LOAD_ERROR_MESSAGE = 'Unable to load campaigns. Please refresh.'
/** Matches demographic / library selected-tab blue used across Sensemaker. */
const TAB_ACTIVE = '#008CD0'

type ResearchTab = 'campaigns' | 'direct'

type CampaignListBodyProps = Readonly<{
  isError: boolean
  filteredItems: Campaign[]
  paginatedItems: Campaign[]
  canCreate: boolean
  statusFilter: CampaignLandingFilter
  campaignSearch: string
  page: number
  rowsPerPage: number
  onRetry: () => void
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
}>

function CampaignListBody({
  isError,
  filteredItems,
  paginatedItems,
  canCreate,
  statusFilter,
  campaignSearch,
  page,
  rowsPerPage,
  onRetry,
  onPageChange,
  onRowsPerPageChange,
}: CampaignListBodyProps) {
  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 8,
        }}
        data-testid="campaign-list-load-error"
      >
        <Button
          variant="contained"
          onClick={onRetry}
          data-testid="campaign-list-retry"
          sx={{ textTransform: 'none', borderRadius: '8px' }}
        >
          Retry
        </Button>
      </Box>
    )
  }

  if (filteredItems.length === 0) {
    return (
      <CampaignListEmptyState
        isAdmin={canCreate}
        statusFilter={statusFilter}
        searchQuery={campaignSearch}
      />
    )
  }

  return (
    <>
      <Box
        data-testid="campaign-list-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          alignItems: 'stretch',
          gap: '32px',
        }}
      >
        {paginatedItems.map((campaign) => (
          <CampaignCard key={campaign.id} campaign={campaign} />
        ))}
      </Box>

      {filteredItems.length > 6 ? (
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            onRowsPerPageChange(Number.parseInt(event.target.value, 10))
          }}
          rowsPerPageOptions={[6, 12, 18]}
          labelRowsPerPage="Rows per page:"
          data-testid="campaign-list-pagination"
          sx={{
            mt: 2,
            borderTop: 'none',
            '& .MuiTablePagination-toolbar': {
              minHeight: 52,
              px: 0,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '14px',
              color: '#4a5565',
            },
            '& .MuiTablePagination-select': {
              borderRadius: '10px',
              bgcolor: '#f1f5f9',
            },
          }}
        />
      ) : null}
    </>
  )
}

export function CampaignListPage() {
  const { showToast } = useToast()
  const role = useSessionStore((s) => s.user?.role)
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading, isError, error, refetch } = useCampaigns()
  const errorToastShownRef = useRef(false)

  const tabFromUrl = searchParams.get('tab') === 'direct' ? 'direct' : 'campaigns'
  const [researchTab, setResearchTab] = useState<ResearchTab>(tabFromUrl)
  const [statusFilter, setStatusFilter] = useState<CampaignLandingFilter>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const { search: campaignSearch, setSearch: setCampaignSearch } = useCampaignLandingSearch()
  const [searchInput, setSearchInput] = useState(campaignSearch)

  const canCreate = role === 'ADMIN' || role === 'EDITOR'
  const hasCampaigns = (data?.items?.length ?? 0) > 0
  const isDirectTab = researchTab === 'direct'
  useDocumentTitle(isDirectTab ? 'Instruments' : 'Campaigns')

  useEffect(() => {
    setResearchTab(tabFromUrl)
  }, [tabFromUrl])

  useEffect(() => {
    setSearchInput(campaignSearch)
  }, [campaignSearch])

  useEffect(() => {
    const handle = globalThis.setTimeout(() => {
      setCampaignSearch(searchInput)
    }, 300)
    return () => globalThis.clearTimeout(handle)
  }, [searchInput, setCampaignSearch])

  const handleResearchTabChange = (_: unknown, value: ResearchTab) => {
    setResearchTab(value)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === 'direct') next.set('tab', 'direct')
        else next.delete('tab')
        return next
      },
      { replace: true },
    )
  }

  useEffect(() => {
    setPage(0)
  }, [campaignSearch])

  useEffect(() => {
    if (!isError) {
      errorToastShownRef.current = false
      return
    }
    if (errorToastShownRef.current) return
    errorToastShownRef.current = true
    showToast(messageFromUnknownApiError(error, LOAD_ERROR_MESSAGE), 'error', 10_000)
  }, [error, isError, showToast])

  const filteredItems = useMemo(() => {
    const items = data?.items ?? []
    return sortCampaignsByUpdatedDesc(items).filter(
      (campaign) =>
        matchesLandingFilter(campaign, statusFilter) &&
        matchesCampaignLandingSearch(campaign, campaignSearch),
    )
  }, [data?.items, statusFilter, campaignSearch])

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage
    return filteredItems.slice(start, start + rowsPerPage)
  }, [filteredItems, page, rowsPerPage])

  const paginatedCampaignIds = useMemo(
    () => paginatedItems.map((campaign) => campaign.id),
    [paginatedItems],
  )
  const { metricsByCampaignId } = useCampaignListInstrumentMetrics(paginatedCampaignIds)
  const displayItems = useMemo(
    () => enrichCampaignsWithInstrumentMetrics(paginatedItems, metricsByCampaignId),
    [paginatedItems, metricsByCampaignId],
  )

  if (isLoading) {
    return <CampaignListPageSkeleton />
  }

  const handleFilterChange = (value: CampaignLandingFilter) => {
    setStatusFilter(value)
    setPage(0)
  }

  const toolbar = isDirectTab ? (
    <CampaignListToolbar
      canCreate={canCreate}
      to="/instruments/create?origin=quick"
      label="Build New Instrument"
      testId="direct-instrument-build-new"
    />
  ) : (
    <CampaignListToolbar
      canCreate={canCreate}
      to="/campaigns/new/setup"
      label="Build New Research"
      testId="campaign-list-create"
    />
  )

  return (
    <Box sx={{ position: 'relative' }} data-testid="campaign-list-root">
      <StandardPageTitleRow marginBottomPx={0} actions={toolbar}>
        <SensemakerPageTitle
          title={isDirectTab ? 'Instruments' : 'Campaigns'}
          description={
            isDirectTab
              ? 'Manage and monitor all your direct instruments'
              : 'Manage and monitor all your campaigns'
          }
        />
      </StandardPageTitleRow>

      <Tabs
        value={researchTab}
        onChange={handleResearchTabChange}
        aria-label="Research views"
        data-testid="research-tabs"
        sx={{
          mt: 2,
          minHeight: 44,
          borderBottom: '1px solid #E2E8F0',
          '& .MuiTabs-indicator': { bgcolor: TAB_ACTIVE, height: 2 },
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 44,
            fontWeight: 700,
            fontSize: { xs: '14px', sm: '16px' },
            color: '#64748B',
            px: 1,
            mr: 3,
            '&.Mui-selected': { color: TAB_ACTIVE },
          },
        }}
      >
        <Tab
          value="campaigns"
          icon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Campaigns"
          data-testid="research-tab-campaigns"
        />
        <Tab
          value="direct"
          icon={<BoltOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Direct Instrument Building"
          data-testid="research-tab-direct"
        />
      </Tabs>

      {isDirectTab ? (
        <Box sx={{ mt: 3 }}>
          <DirectInstrumentBuildingPanel canMutate={canCreate} />
        </Box>
      ) : (
        <>
          {hasCampaigns ? (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { lg: 'center' },
                gap: 2,
              }}
            >
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by Campaign"
                data-testid="campaign-list-search"
                size="small"
                sx={{
                  width: { xs: '100%', lg: 320 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    fontSize: '14px',
                  },
                }}
                inputProps={{
                  'aria-label': 'Search by Campaign',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <CampaignStatusFilter value={statusFilter} onChange={handleFilterChange} />
            </Box>
          ) : null}

          <Box sx={{ mt: hasCampaigns ? '32px' : '16px' }}>
            <CampaignListBody
              isError={isError}
              filteredItems={filteredItems}
              paginatedItems={displayItems}
              canCreate={canCreate}
              statusFilter={statusFilter}
              campaignSearch={campaignSearch}
              page={page}
              rowsPerPage={rowsPerPage}
              onRetry={() => {
                Promise.resolve(refetch()).catch(() => undefined)
              }}
              onPageChange={setPage}
              onRowsPerPageChange={(nextRowsPerPage) => {
                setRowsPerPage(nextRowsPerPage)
                setPage(0)
              }}
            />
          </Box>
        </>
      )}
    </Box>
  )
}
